import React, { useState } from 'react';
import { Box, Button, FormControlLabel, Switch, Typography } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { adminAPI } from '../../services/api';
import Panel from '../crt/Panel';

const LEVEL_COLORS: Record<string, string> = {
  ERROR: '#ff3344',
  WARN: '#ffb000',
  INFO: '#00ff41',
  DEBUG: '#5aaa5a',
};

const LogsTab: React.FC = () => {
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [lines, setLines] = useState(100);

  const { data, refetch, isFetching } = useQuery({
    queryKey: ['admin-logs', lines],
    queryFn: () => adminAPI.getLogs(lines),
    staleTime: 10000,
    refetchInterval: autoRefresh ? 15000 : false,
  });

  const entries = data?.lines || [];

  return (
    <Panel label="RECENT ACTIVITY">
      <Box display="flex" gap={2} mb={2} alignItems="center">
        <FormControlLabel
          control={
            <Switch
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              size="small"
            />
          }
          label={<Typography variant="caption">AUTO-REFRESH (15s)</Typography>}
        />
        <Button size="small" variant="outlined" disabled={isFetching} onClick={() => refetch()}>
          {isFetching ? '[ ... ]' : '[ REFRESH ]'}
        </Button>
        <Button
          size="small"
          variant="outlined"
          disabled={lines >= 500}
          onClick={() => setLines(Math.min(500, lines + 100))}
        >
          [ MORE LINES ]
        </Button>
        <Typography variant="caption" color="textSecondary" sx={{ ml: 'auto' }}>
          {entries.length} entries · source: {data?.source || '—'}
        </Typography>
      </Box>

      {data?.note && (
        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 1 }}>
          {data.note}
        </Typography>
      )}

      <Box
        component="pre"
        sx={{
          m: 0,
          p: 2,
          maxHeight: 600,
          overflow: 'auto',
          backgroundColor: 'var(--crt-bg)',
          border: '1px solid var(--crt-border)',
          fontFamily: "'VT323', 'Share Tech Mono', monospace",
          fontSize: '15px',
          lineHeight: 1.4,
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {entries.length === 0 ? (
          <Typography color="textSecondary">No activity in window.</Typography>
        ) : (
          entries.map((e: any) => (
            <Box key={e.id} sx={{ display: 'flex', gap: 1 }}>
              <Box component="span" sx={{ color: 'var(--crt-dim)', minWidth: 170 }}>
                {e.ts ? new Date(e.ts).toLocaleTimeString() + ' ' + (e.ts || '').substring(0, 10) : ''}
              </Box>
              <Box component="span" sx={{ color: LEVEL_COLORS[e.level] || 'var(--crt-green)', minWidth: 50 }}>
                {e.level}
              </Box>
              <Box component="span">{e.text}</Box>
            </Box>
          ))
        )}
      </Box>
    </Panel>
  );
};

export default LogsTab;
